import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  FolderKanban,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Zap,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import {
  useActionList,
  useDeleteAction,
  useUpdateActionStatus,
} from "../hooks/useActions";
import { useDecisionList } from "@/modules/decisions/hooks/useDecisions";
import { useMilestoneList } from "@/modules/milestones/hooks/useMilestones";
import { NewActionDrawer } from "../components/NewActionDrawer";
import { ActionStatusBadge } from "../components/ActionStatusBadge";
import { ActionPriorityBadge } from "../components/ActionPriorityBadge";
import { ActionSourceBadge } from "../components/ActionSourceBadge";
import type { ActionListItem, ActionStatus } from "../types";

const BORDER = "hsl(210,30%,85%)";

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + "…" : s;
}

const ACTION_COLUMNS: ColumnDef[] = [
  { key: "code", label: "#" },
  { key: "title", label: "Title" },
  { key: "source", label: "Source" },
  { key: "projectName", label: "Project" },
  { key: "taskCode", label: "Task", defaultVisible: false },
  { key: "priority", label: "Priority" },
  { key: "status", label: "Status" },
  { key: "dueDate", label: "Due Date" },
  { key: "assigneeName", label: "Assignee" },
];

const STATUS_OPTIONS: { value: ActionStatus; label: string }[] = [
  { value: "OPEN", label: "Open" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

const iconBtnBase =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-muted hover:text-foreground";
const iconBtnDelete =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-[hsl(0,70%,95%)] hover:text-[hsl(0,84%,50%)]";

export default function ActionListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("");
  const [milestoneFilter, setMilestoneFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [statusPopover, setStatusPopover] = useState<string | null>(null);

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    isVisible,
    minReached,
  } = useColumnVisibility("actions", ACTION_COLUMNS);
  const exportFilters = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter || undefined,
      priority: priorityFilter || undefined,
      source: sourceFilter || undefined,
      decisionId: decisionFilter || undefined,
      milestoneId: milestoneFilter || undefined,
    }),
    [
      search,
      statusFilter,
      priorityFilter,
      sourceFilter,
      decisionFilter,
      milestoneFilter,
    ],
  );

  const { data, isLoading, isError, error } = useActionList({
    page,
    limit: pageSize,
    search: search || undefined,
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
    source: sourceFilter || undefined,
    decisionId: decisionFilter || undefined,
    milestoneId: milestoneFilter || undefined,
  });
  const { data: decisionsResult } = useDecisionList({ page: 1, limit: 100 });
  const decisions = decisionsResult?.data ?? [];
  const { data: milestonesResult } = useMilestoneList({ page: 1, limit: 100 });
  const milestones = milestonesResult?.data ?? [];

  const updateStatus = useUpdateActionStatus();
  const deleteAction = useDeleteAction();

  const actions = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, data?.pagination.totalPages ?? 1);
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  async function handleStatusChange(actionId: string, newStatus: ActionStatus) {
    const res = await updateStatus.mutateAsync({
      id: actionId,
      status: newStatus,
    });
    setStatusPopover(null);
    if (res.allTaskActionsDone && res.taskCode) {
      showToast(
        `All actions done for ${res.taskCode} — task can now be completed`,
      );
    } else {
      showToast(`Status updated to ${newStatus.replace(/_/g, " ")}`);
    }
  }

  function handleDelete(a: ActionListItem) {
    setDeleteTarget({ id: a.id, name: a.title });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteAction.mutateAsync(deleteTarget.id);
    showToast("Action deleted");
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Daily Work" },
        { label: "Actions" },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">Actions</h1>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton
              module="actions"
              filters={exportFilters}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={ACTION_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Action
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load actions</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px] shrink-0">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search actions..."
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) => resetPage(setStatusFilter)(e.target.value)}
                className="w-40 shrink-0"
              >
                <option value="">All Status</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
              <Select
                value={priorityFilter}
                onChange={(e) => resetPage(setPriorityFilter)(e.target.value)}
                className="w-40 shrink-0"
              >
                <option value="">All Priority</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </Select>
              <Select
                value={sourceFilter}
                onChange={(e) => resetPage(setSourceFilter)(e.target.value)}
                className="w-40 shrink-0"
              >
                <option value="">All Sources</option>
                <option value="MANUAL">Manual</option>
                <option value="MEETING">Meeting</option>
                <option value="TASK">Task</option>
              </Select>
              <Select
                value={decisionFilter}
                onChange={(e) => resetPage(setDecisionFilter)(e.target.value)}
                className="w-48 shrink-0"
              >
                <option value="">All Decisions</option>
                {decisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.code} — {d.title}
                  </option>
                ))}
              </Select>
              <Select
                value={milestoneFilter}
                onChange={(e) => resetPage(setMilestoneFilter)(e.target.value)}
                className="w-48 shrink-0"
              >
                <option value="">All Milestones</option>
                {milestones.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.code} — {m.title}
                  </option>
                ))}
              </Select>
            </div>

            <div
              className="overflow-hidden rounded-xl border bg-card"
              style={{ borderColor: BORDER }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    strokeWidth={1.5}
                  />
                  Loading actions…
                </div>
              ) : actions.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  No actions match the current filters.
                </div>
              ) : (
                <table
                  className="w-full text-[13px]"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {isVisible("code") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                          #
                        </th>
                      )}
                      {isVisible("title") && (
                        <th className="px-[14px] py-[9px]">Title</th>
                      )}
                      {isVisible("source") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 90 }}>
                          Source
                        </th>
                      )}
                      {isVisible("projectName") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 150 }}>
                          Project
                        </th>
                      )}
                      {isVisible("taskCode") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 100 }}>
                          Task
                        </th>
                      )}
                      {isVisible("priority") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 90 }}>
                          Priority
                        </th>
                      )}
                      {isVisible("status") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                          Status
                        </th>
                      )}
                      {isVisible("dueDate") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 120 }}>
                          Due Date
                        </th>
                      )}
                      {isVisible("assigneeName") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 130 }}>
                          Assignee
                        </th>
                      )}
                      <th
                        className="px-[14px] py-[9px] text-right"
                        style={{ width: 130 }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {actions.map((a) => {
                      const isOverdue =
                        !!a.dueDate &&
                        new Date(a.dueDate) < new Date() &&
                        a.status !== "COMPLETED" &&
                        a.status !== "CANCELLED";
                      const contextName =
                        a.projectName ?? a.companyName ?? a.productName ?? null;
                      return (
                        <tr
                          key={a.id}
                          className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                          onClick={() => navigate(`/actions/${a.id}`)}
                        >
                          {isVisible("code") && (
                            <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                              {a.code}
                            </td>
                          )}
                          {isVisible("title") && (
                            <td className="px-[14px] py-[11px]">
                              <Link
                                to={`/actions/${a.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="font-medium text-foreground hover:text-[hsl(210,100%,35%)]"
                              >
                                {truncate(a.title, 50)}
                              </Link>
                            </td>
                          )}
                          {isVisible("source") && (
                            <td className="px-[14px] py-[11px]">
                              <ActionSourceBadge source={a.source} />
                            </td>
                          )}
                          {isVisible("projectName") && (
                            <td className="px-[14px] py-[11px] text-muted-foreground">
                              {contextName ? (
                                <span className="inline-flex items-center gap-1.5">
                                  <FolderKanban
                                    className="h-3 w-3"
                                    strokeWidth={1.5}
                                  />
                                  <span className="truncate">{contextName}</span>
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}
                          {isVisible("taskCode") && (
                            <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                              {a.taskCode ?? "—"}
                            </td>
                          )}
                          {isVisible("priority") && (
                            <td className="px-[14px] py-[11px]">
                              <ActionPriorityBadge priority={a.priority} />
                            </td>
                          )}
                          {isVisible("status") && (
                            <td className="px-[14px] py-[11px]">
                              <ActionStatusBadge status={a.status} />
                            </td>
                          )}
                          {isVisible("dueDate") && (
                            <td className="px-[14px] py-[11px]">
                              {a.dueDate ? (
                                <span className="inline-flex items-center gap-2">
                                  <span
                                    className={
                                      isOverdue
                                        ? "font-medium text-[hsl(0,70%,45%)]"
                                        : "text-foreground"
                                    }
                                  >
                                    {formatDate(a.dueDate)}
                                  </span>
                                  {isOverdue && (
                                    <span
                                      className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
                                      style={{
                                        background: "hsl(0,70%,94%)",
                                        color: "hsl(0,70%,40%)",
                                      }}
                                    >
                                      Overdue
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          )}
                          {isVisible("assigneeName") && (
                            <td className="px-[14px] py-[11px]">
                              {a.assignedToName ? (
                                <span className="inline-flex items-center gap-2">
                                  <span
                                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                                    style={{
                                      background: "hsl(210,55%,92%)",
                                      color: "hsl(210,100%,28%)",
                                    }}
                                  >
                                    {initialsOf(a.assignedToName)}
                                  </span>
                                  <span className="text-foreground">
                                    {a.assignedToName}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-muted-foreground">
                                  Unassigned
                                </span>
                              )}
                            </td>
                          )}
                          <td
                            className="relative px-[14px] py-[11px] text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="View"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/actions/${a.id}`);
                                }}
                              >
                                <Eye
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="Edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/actions/${a.id}`);
                                }}
                              >
                                <Pencil
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="Change Status"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStatusPopover(
                                    statusPopover === a.id ? null : a.id,
                                  );
                                }}
                              >
                                <RefreshCw
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                              <button
                                type="button"
                                className={iconBtnDelete}
                                title="Delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(a);
                                }}
                              >
                                <Trash2
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                            </div>
                            {statusPopover === a.id && (
                              <div
                                className="absolute right-4 top-10 z-20 w-56 rounded-lg border bg-white p-2 shadow-lg"
                                style={{ borderColor: BORDER }}
                              >
                                {STATUS_OPTIONS.map((opt) => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() =>
                                      handleStatusChange(a.id, opt.value)
                                    }
                                    className="block w-full rounded px-3 py-1.5 text-left text-sm text-foreground hover:bg-[hsl(210,50%,96%)]"
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              <div
                className="flex items-center justify-between border-t bg-[hsl(210,30%,97%)] px-[14px] py-[9px] text-xs text-muted-foreground"
                style={{ borderColor: BORDER }}
              >
                <div className="flex items-center gap-3">
                  <span>
                    Showing {start}-{end} of {total} entries
                  </span>
                  <span className="flex items-center gap-1.5">
                    Show
                    <Select
                      value={String(pageSize)}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="h-7 w-[68px] text-xs"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </Select>
                    entries
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <span className="px-2 text-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <NewActionDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={(code) => showToast(`Action created: ${code}`)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete action?"
        description={
          deleteTarget
            ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Actions list.`
            : ""
        }
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}
